import { useState } from "react";
import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";

import { Card, Table } from "@cofano/cck";

import { usePermission } from "components/utils/Permission";
import { TableId, useBackendTable } from "components/table";
import Language from "components/utils/Language";

import { FormattedDateWindow } from "utils/DateUtils";
import { LocationLink, ShipLink } from "utils/LinkUtils";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import PushingTugPlanningPlanPushedBargesDialog from "view/pushingtug/components/dialogs/PushingTugPlanningPlanPushedBargesDialog";
import { PushedBargeVoyage, PushingTugVoyage } from "view/pushingtug/utils/PushingTugPlanningUtils";

import { BargeType, SortDirection } from "__generated__/enums";
import { GetPushedBargeVoyagesDocument } from "__generated__/view/pushingtug/graphql/pushingTug.queries.generated";

interface PushingTugPlanningBargeVoyagesProps {
	pushingTugVoyage: PushingTugVoyage;
	onPlanningSaved: () => void;
}

const PushingTugPlanningBargeVoyages = ({ pushingTugVoyage, onPlanningSaved }: PushingTugPlanningBargeVoyagesProps) => {

	const client = useApolloClient();

	const [ openPlanningDialog, setOpenPlanningDialog ] = useState(false);
	const [ pushedBargeVoyages, setPushedBargeVoyages ] = useState<PushedBargeVoyage[]>([]);

	const { isSkipperOrShipManager } = usePermission();

	const { tableProps, refetch, clearSelection } = useBackendTable({
		id: TableId.PushedBargeVoyages,
		selectSettings: {
			isFullSelectEnabled: false,
		},
		onSelect: (rows) => {
			setPushedBargeVoyages(rows as PushedBargeVoyage[]);
		},
		onChange: async (pagination, sort, filters, callback) => {
			try {
				const result = await client.query({
					query: GetPushedBargeVoyagesDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "created",
								direction: SortDirection.Desc,
							} ],
						},
						search: {
							pushingTugVoyageId: pushingTugVoyage.id,
							shipIds: filters.ship?.map((option: ShipSelectOption) => option.id),
						},
					},
				});

				callback({
					content: result.data.pushedBargeVoyages.content,
					totalResults: result.data.pushedBargeVoyages.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	return (
		<Card
			disablePadding
			title={Language("COMPATIBLE_PUSHED_BARGE_VOYAGES")}
			actions={[ {
				onClick: () => setOpenPlanningDialog(true),
				text: intl.formatMessage({ id: "PLAN" }),
				disabled: !(pushedBargeVoyages.length > 0),
			} ]}>
			<Table
				{...tableProps}
				columns={[ {
					id: "voyageNumber",
					title: intl.formatMessage({ id: "VOYAGES_VOYAGE_NUMBER" }),
					sortable: true,
					width: 200,
				}, {
					id: "ship",
					title: intl.formatMessage({ id: "VOYAGES_SHIP" }),
					sortable: true,
					sortKey: "ship.name",
					render: (row: PushedBargeVoyage) => <ShipLink ship={row.ship} />,
					width: 200,
					filterComponent: !isSkipperOrShipManager ? (value, onChange) => (
						<ShipSelect
							label={intl.formatMessage({ id: "SHIPS" })}
							value={value}
							onChange={options => onChange(options)}
							additionalSearchOptions={{
								includeBargeTypes: [ BargeType.PushedBarge ],
							}}
							multiple
						/>
					) : undefined,
				}, {
					id: "from",
					title: intl.formatMessage({ id: "VOYAGE_STARTLOCATION" }),
					sortable: true,
					render: (row: PushedBargeVoyage) => <LocationLink location={row.from} />,
				}, {
					id: "to",
					title: intl.formatMessage({ id: "VOYAGE_ENDLOCATION" }),
					sortable: true,
					render: (row: PushedBargeVoyage) => <LocationLink location={row.to} />,
				}, {
					id: "start",
					title: intl.formatMessage({ id: "START_END_WINDOW" }),
					sortable: true,
					render: (row: PushedBargeVoyage) => <FormattedDateWindow from={row.start} to={row.end} withoutYear />,
				} ]}
			/>

			{openPlanningDialog && (
				<PushingTugPlanningPlanPushedBargesDialog
					refetch={() => {
						refetch();
						onPlanningSaved();
						clearSelection();
					}}
					open={openPlanningDialog}
					pushingTugVoyage={pushingTugVoyage}
					selectedPushingBargeVoyages={pushedBargeVoyages}
					onClose={() => setOpenPlanningDialog(false)}
				/>
			)}
		</Card>
	);
};

export default PushingTugPlanningBargeVoyages;
